/**
 * Chat realtime envelope decoder.
 *
 * Mirrors internal/chat/realtime/envelope.go. The frontend never
 * emits envelopes; it only decodes server→client frames.
 */

export interface ChatRealtimeMessage {
  readonly id: string;
  readonly channelId: string;
  readonly senderAccountId: string;
  readonly content: string;
  readonly createdAt: string;
}

export interface ChatMessageCreatedEvent {
  readonly type: 'chat.message.created';
  readonly eventId: string;
  readonly channelId: string;
  readonly message: ChatRealtimeMessage;
  readonly at: string;
}

export interface ConnectionReadyEvent {
  readonly type: 'connection.ready';
  readonly at: string;
}

export type RealtimeEvent = ChatMessageCreatedEvent | ConnectionReadyEvent;

export const ErrInvalidEnvelope = Symbol('ErrInvalidEnvelope');
export const ErrUnknownEnvelope = Symbol('ErrUnknownEnvelope');

export function DecodeEnvelope(raw: string): RealtimeEvent {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw ErrInvalidEnvelope;
  }
  if (!parsed || typeof parsed !== 'object') {
    throw ErrInvalidEnvelope;
  }
  const ev = parsed as Record<string, unknown>;
  if (ev.type === 'connection.ready') {
    return { type: 'connection.ready', at: String(ev.at ?? '') };
  }
  if (ev.type === 'chat.message.created') {
    const message = ev.message as Record<string, unknown> | undefined;
    if (!message || typeof message !== 'object') {
      throw ErrInvalidEnvelope;
    }
    if (
      typeof message.id !== 'string' ||
      typeof message.channel_id !== 'string' ||
      typeof message.sender_account_id !== 'string' ||
      typeof message.content !== 'string' ||
      typeof message.created_at !== 'string'
    ) {
      throw ErrInvalidEnvelope;
    }
    return {
      type: 'chat.message.created',
      eventId: String(ev.event_id ?? message.id),
      channelId: String(message.channel_id),
      message: {
        id: message.id,
        channelId: message.channel_id,
        senderAccountId: message.sender_account_id,
        content: message.content,
        createdAt: message.created_at,
      },
      at: String(ev.at ?? ''),
    };
  }
  throw ErrUnknownEnvelope;
}
